import { Component, EventEmitter, Inject, Output, ViewChild } from '@angular/core';

import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';

import { BootstrapPopupComponent } from '../../../shared/bootstrap-popup/components/bootstrap-popup.component';
import { MessageDialogComponent } from '../../../shared/dialogs/components/message-dialog.component';
import { LocalDate } from '../../../shared/jsr310-localdates/types/local-date.type';

import { MemoService } from '../services/memo.service';
import { Memo } from '../entities/memo.entity';

import { ParcService } from '../../structure/parc/services/parc.service';
import { ParcTLR } from '../../structure/parc/entities/parc-tlr.entity';

import { OuvrageService } from '../../structure/ouvrage/services/ouvrage.service';
import { OuvrageTLR } from '../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { AntenneService } from '../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../structure/antenne/entities/antenne-tlr.entity';

import { AccesService } from '../../acces/acces/services/acces.service';
import { AccesTLR } from '../../acces/acces/entities/acces-tlr.entity';

import { StaticTypeService } from '../../../static-types/services/static-types.service';
import { MemoCategory } from '../../../static-types/entities/memo-category.entity';
import { MemoSubCategory } from '../../../static-types/entities/memo-sub-category.entity';
import { MemoLevel } from '../../../static-types/entities/memo-level.entity';
import { EventBusService, Events } from '../../../technical-modules/events/services/event-bus.service';
import { OuvrageAccesDTO } from '../../acces/acces/entities/ouvrage-acces-dto.entity';

enum Mode {
	CREATION,
	UPDATE,
	ARCHIVE,
	CREATION_AFTER_FERMETURE_ACCES
}

@Component({
	selector: 'edit-memo-dialog',
	templateUrl: 'edit-memo-dialog.html',
	styleUrls: ['edit-memo-dialog.css']
})
export class EditMemoDialogComponent {
	@ViewChild(BootstrapPopupComponent) mainFrame!: BootstrapPopupComponent;

	modes = Mode;
	
	// Gestion globale de la fenetre
	title!: string;
	mode!: Mode;
	memo!: Memo;
	categories!: MemoCategory[];
	sousCategories: MemoSubCategory[] = [];
	niveaux!: MemoLevel[];

	// Valeur actuelles des champs de saisie
	numero: string = '';
	antenneName: string = '';
	parcName: string = '';
	ouvrageNames: string = '';
	antenneId: number | null = null;
	parcId!: number | undefined;
	ouvrageIds: number[] = [];
	intitule: string = '';
	selectedCategorieId!: number | null;
	selectedSousCategorieId!: number | null;
	selectedNiveauId!: number | null;
	dateDebut: LocalDate | null = null;
	dateFin: LocalDate | null = null;
	commentaire: string = '';
	parcRestrictions = {};
	
	reinitChoixOuvragesSubject = new Subject<void>();
	suggestableOuvrages: OuvrageTLR[] = [];
	isOuvragePreselected = (ouvrage: OuvrageTLR) => this.ouvrageIds.includes(ouvrage.id);
	getOuvrageLabel = (ouvrage: OuvrageTLR) => {
		if (ouvrage != undefined) {
			return ouvrage.nom + (ouvrage.description ? ' (' + ouvrage.description + ')' : '');
		} else {
			return '';
		}
	};

	constructor( //
		private dialog: MatDialog,
		@Inject(MemoService) private memoService: MemoService,
		@Inject(AntenneService) private antenneService: AntenneService,
		@Inject(ParcService) private parcService: ParcService,
		@Inject(OuvrageService) private ouvrageService: OuvrageService,
		@Inject(StaticTypeService) private staticTypeService: StaticTypeService,
		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(AccesService) private accesService: AccesService,
	) {
		this.staticTypeService.getMemoCategoryList().subscribe(
			(list: MemoCategory[]) => this.categories = list
		);
		this.staticTypeService.getMemoLevelList().subscribe(
			(list: MemoLevel[]) => this.niveaux = list
		);
		eventBusService.observe(Events.CreateMemoModalRequested, (accesId: number) => {
			console.log('on a demandé de prédéfinir le memo');
			this.accesService.getById(accesId).subscribe((acces: AccesTLR) => {
				this.openForCreationAfterAccesClose(acces);
			});
			
			
		});
	}

	// Evenements de validation de l'operation en cours
	@Output() memoCreate = new EventEmitter<Memo>();
	@Output() memoUpdate = new EventEmitter<Memo>();
	@Output() memoArchive = new EventEmitter<Memo>();
	
	/////////////////////////
	// Actions utilisateur //
	/////////////////////////

	openForCreation() {
		// Definition du titre de la pop-up
		this.title = 'Création de mémo';
		this.mode = Mode.CREATION;

		// Memorisation du memo dans son etat d'avant la saisie utilisateur
		this.memo = new Memo();

		// Initialisation des champs de saisie
		// NB: le composant antenne-to-ouvrages-selector se chargera de definir l'antenne en fonction du parc
		this.parcId = undefined;
		this.ouvrageIds = [];
		this.intitule = '',
		this.selectedCategorieId = null;
		this.onSelectCategorieId();
		this.selectedSousCategorieId = null;
		this.selectedNiveauId = null;
		this.dateDebut = null;
		this.dateFin = null;
		this.commentaire = '';

		// Mise a jour de l'indicatif du memo
		this.numero = '';

		// Affichage de la pop-up
		this.mainFrame.open();
	}

	openForCreationAfterAccesClose(acces?: AccesTLR) {

		// Définition du titre de la pop-up
		this.title = 'Création de mémo';
		this.mode = Mode.CREATION_AFTER_FERMETURE_ACCES;

		// Memorisation du memo dans son etat d'avant la saisie utilisateur
		this.memo = new Memo();
		
		// Initialisation des champs de saisie
		// NB: le composant antenne-to-ouvrages-selector se chargera de definir l'antenne en fonction du parc
		this.intitule = '';
		this.selectedCategorieId = null;
		this.onSelectCategorieId();
		this.selectedSousCategorieId = null;
		this.selectedNiveauId = null;
		this.commentaire = '';

		// Pre-remplissage 
		if (acces) {
			this.parcId = acces.parcId;
			
			let parc$ = this.parcService.getParcTLRById(acces.parcId);
			let accesOuvrages$ = this.accesService.getOuvrageDTOByAcces(acces.id);
			
			let memoData$ = combineLatest([parc$, accesOuvrages$]).pipe(
				mergeMap(
					([parc, accesOuvrages]) => {
						let antennes$ = this.antenneService.getAntenneTLRById(parc.antenneId);
						let ouvragesDuParcs$ = this.ouvrageService.getOuvrageTLRByParc(parc.id);
						return forkJoin({
							parc: of(parc),
							antenne: antennes$,
							accesOuvrages: of(accesOuvrages),
							ouvragesDuParcs: ouvragesDuParcs$
						});
					}
				)
			);
			memoData$.subscribe(
				(memoData) => {
					this.parcName = memoData.parc.code + ' (' + memoData.parc.description + ')';
					this.antenneName = memoData.antenne.nom;
					this.suggestableOuvrages = memoData.ouvragesDuParcs;
					this.ouvrageIds = [];
					this.ouvrageNames = '';
					let sep = '';
					memoData.accesOuvrages.forEach ( ao => {
						this.ouvrageIds.push(ao.id);
						this.ouvrageNames = this.ouvrageNames + sep + ao.nom;
						sep = ', ';
					} );
					this.reinitChoixOuvragesSubject.next();
			});
			
			if (acces.dateFermeture) {
				let dateDebut = acces.dateFermeture.slice(0,3);
				this.dateDebut = (dateDebut as any as LocalDate);
			}
			this.commentaire = acces.commentaire ?? '';
		} else {
			this.parcId = undefined;
			this.dateDebut = null;
			this.dateFin = null;
			this.parcName = '';
			this.antenneName = '';
		}

		// Mise a jour de l'indicatif du memo
		this.numero = '';

		// Affichage de la pop-up
		this.mainFrame.open();
	}

	openForUpdate(memo: Memo) {
		this.displayMemo("Modification du mémo", Mode.UPDATE, memo);
	}

	openForArchive(memo: Memo) {
		this.displayMemo("Archivage du mémo", Mode.ARCHIVE, memo);
	}

	private displayMemo(title: string, mode: Mode, memo: Memo) {
		// Definition du titre de la pop-up
		this.title = title

		// Definition du mode d'affichage
		this.mode = mode;

		// Memorisation du memo dans son etat d'avant la saisie utilisateur
		this.memo = memo;

		// Initialisation des champs de saisie
		this.parcId = memo.parcId;
		// NB: le composant antenne-to-ouvrages-selector se chargera de definir l'antenne en fonction du parc
		this.ouvrageIds = memo.ouvrageIds;
		this.intitule = memo.intitule,
		this.selectedCategorieId = memo.categorieId;
		this.onSelectCategorieId();
		this.selectedSousCategorieId = memo.sousCategorieId;
		this.selectedNiveauId = memo.niveauId;
		this.dateDebut = memo.dateDebut;
		this.dateFin = memo.dateFin;
		this.commentaire = memo.commentaire;

		// Mise a jour de l'indicatif du memo et de l'antenne par defaut
		this.parcService.getParcTLRById(memo.parcId).subscribe(
			(parc: ParcTLR) => {
				this.parcName = '';
				this.numero = 'Mem' + memo.id + ' - ' + parc.description;
				this.parcName = parc.code + ' (' + parc.description + ')';
				this.antenneName = '';
				this.antenneService.getAntenneTLRById(parc.antenneId).subscribe(
					(antenne: AntenneTLR) => {
						this.antenneName = antenne.nom;
					} 
				);
			} 
		);
		
		this.ouvrageNames = '';
		this.ouvrageIds.forEach( oId => {
			this.ouvrageService.getOuvrageTLRById(oId).subscribe(
				(ouvrage: OuvrageTLR) => {
					this.ouvrageNames = this.ouvrageNames + (this.ouvrageNames.length ? ', ' : '') + ouvrage.nom;
				} 
			);
		} );

		// Affichage de la pop-up
		this.mainFrame.open();
	}
	
	onAntenneIdChange(antenneId: number | null) {
		this.antenneId = antenneId;
	}

	onSelectCategorieId() {
		let categorieId = this.selectedCategorieId;
		this.selectedSousCategorieId = null;
		if (categorieId) {
			this.staticTypeService.getMemoSubCategoriesByParent(categorieId).subscribe(
				(list: MemoSubCategory[]) => this.sousCategories = list
			);
		} else {
			this.sousCategories = [];
		}
	}

	onCreate() {
		// Validation de la saisie utilisateur
		if (!this.validateInputs()) {
			return;
		}

		// Mise a jour de l'entite
		this.updateEntityWithInputs();

		// Envoi de la demande de sauvegarde de la nouvelle version de l'entite
		this.memoService.createMemo(this.memo) //
			.pipe(
				catchError(
					(err: any, _caught: Observable<Memo>) => {
						alert("Erreur lors de l'enregistrement du memo (Erreur " + err.status + ")");
						return of(null);
					}
				)
			)
			.subscribe(
				(memo: Memo | null) => {
					if (memo) {
						this.memo = memo;

						// Suite a la creation du memo, il faut afficher a l'utilisateur le numero de celui-ci en toast
						this.parcService.getParcTLRById(this.memo.parcId).subscribe(
							(parc: ParcTLR) => {
								const numero = "mem" + memo.id + " - " + parc.code;
								alert("Le mémo " + numero + " a bien été créé");

								// Puis on ferme la fenetre de creation
								this.mainFrame.close();

								// Et on signale au parent la creation du memo
								this.memoCreate.emit(memo);
							}
						);
					}
				}
			);
	}

	onModify() {
		// Validation de la saisie utilisateur
		if (!this.validateInputs()) {
			return;
		}

		// Mise a jour de l'entite
		this.updateEntityWithInputs();

		// Envoi de la demande de sauvegarde de la nouvelle version de l'entite
		this.memoService.updateMemo(this.memo) //
			.pipe(
				catchError(
					(err: any, _caught: Observable<Memo>) => {
						alert("Erreur lors de l'enregistrement du memo (Erreur " + err.status + ")");
						return of(null);
					}
				)
			)
			.subscribe(
				(memo: Memo | null) => {
					if (memo) {
						this.memo = memo;

						// Puis on ferme la pop-up
						this.mainFrame.close();

						// Et on signale au parent la creation du memo
						this.memoUpdate.emit(memo);
					}
				}
			);
	}

	onArchive() {
		// Validation de la saisie utilisateur
		if (!this.validateInputs()) {
			return;
		}

		// Mise a jour de l'entite
		this.updateEntityWithInputs();
		this.memo.archived = true;

		// Envoi de la demande de sauvegarde de la nouvelle version de l'entite
		this.memoService.updateMemo(this.memo) //
			.pipe(
				catchError(
					(err: any, _caught: Observable<Memo>) => {
						alert("Erreur lors de l'archivage du memo (Erreur " + err.status + ")");
						return of(null);
					}
				)
			)
			.subscribe(
				(memo: Memo | null) => {
					if (memo) {
						this.memo = memo;

						// Puis on ferme la pop-up
						this.mainFrame.close();

						// Et on signale au parent la creation du memo
						this.memoArchive.emit(memo);
					}
				}
			);
	}

	private validateInputs(): boolean {
		let erreurs: string[] = [];

		// Le parc ne peut pas être nul
		if (this.mode === Mode.CREATION && this.isEmpty(this.parcId)) {
			erreurs.push("Le mémo doit référencer un parc");
		}

		// Categorie => RG31 : Le champ ne peut pas être nul
		if (this.mode === Mode.CREATION && this.isEmpty(this.selectedCategorieId)) {
			erreurs.push("Le mémo doit appartenir à une catégorie");
		}

		// Niveau => RG32 : Le champ ne peut pas être nul
		if (this.isEmpty(this.selectedNiveauId)) {
			erreurs.push("Le mémo doit référencer un niveau");
		}

		// RG33 : La date de début ne peut pas être nulle
		if (!this.dateDebut) {
			erreurs.push("Le mémo doit comporter une date de début valide");
		}

		// RG34 : La date de fin peut être nulle
		// Pas de controle a faire selon les RG, mais on verifie quand meme la logique des deux dates
		if (this.dateDebut && this.dateFin) {
			if (!this.isValidDateRange()) {
				erreurs.push("La date de début doit être inférieure a la date de fin");
			}
		}

		// Commentaire => RG35 : Le champ peut être nul, pas de test
		
		// Pas de controle a faire
		if (erreurs.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Création impossible',
					messages: erreurs
				}
			});

			return false;
		}

		return true;
	}

	private isEmpty(value: any): boolean {
		return value === undefined || value === null;
	}

	private isValidDateRange() {
		if (!this.dateDebut || !this.dateFin) {
			return false;
		}

		if (this.dateFin[0] < this.dateDebut[0]) {
			return false;
		}

		if (this.dateFin[0] > this.dateDebut[0]) {
			return true;
		}

		// Arrive ici, on sait que l'annee des deux dates est egale

		if (this.dateFin[1] < this.dateDebut[1]) {
			return false;
		}

		if (this.dateFin[1] > this.dateDebut[1]) {
			return true;
		}

		// Arrive ici, on sait que l'annee et le mois des deux dates sont egaux

		if (this.dateFin[2] < this.dateDebut[2]) {
			return false;
		}

		return true;
	}

	private updateEntityWithInputs() {
		// Fonction servant a passer les erreur de types sur les valeurs qui normalement ont ete non-null par validateInputs() avant d'entrer dans cette fonction
		function assertNonNull<T>(value: T | null | undefined) {
			if (value === null || value === undefined) {
				throw new Error("assertNotNull failed");
			}

			return value;
		}

		const memo = this.memo;
		if (this.mode === Mode.CREATION || this.mode === Mode.CREATION_AFTER_FERMETURE_ACCES) {
			memo.parcId = assertNonNull(this.parcId);
			memo.ouvrageIds = this.ouvrageIds;
		}
		memo.intitule = this.intitule;
		if (this.mode === Mode.CREATION || this.mode === Mode.CREATION_AFTER_FERMETURE_ACCES) {
			memo.categorieId = assertNonNull(this.selectedCategorieId);
			memo.sousCategorieId = this.selectedSousCategorieId;
		}
		memo.niveauId = assertNonNull(this.selectedNiveauId);
		memo.dateDebut = assertNonNull(this.dateDebut);
		memo.dateFin = this.dateFin;
		memo.commentaire = this.commentaire;
	}

	onCancelRequested() {
		this.mainFrame.close();
	}
	
	onOuvrageIdsChange(ouvrages: OuvrageTLR[]) {
		this.ouvrageIds = ouvrages.map( $ => $.id);
	}
}